import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import axios from '../../axios';

import { IPromptFragment } from '../../interfaces/domain';
import { Routes } from '../../classes/Routes';
import Alert from '../../components/UI/Alert/Alert';
import Button from '../../components/UI/Button/Button';
import Container from '../../components/UI/Container/Container';
import Spinner from '../../components/UI/Spinner/Spinner';
import Table from '../../components/UI/Table/Table';
import TableTh from '../../components/UI/Table/TableTh/TableTh';
import { useTableSort } from '../../components/UI/Table/useTableSort';
import { ETranslation } from '../../translations/translation-keys';
import { useAuthUser } from '../../hooks/useAuthUser';
import { useOrganizationOptions } from '../../hooks/useOrganizationOptions';
import Input, { EInputType } from '../../components/UI/Input/Input';

const PromptFragments: React.FC<RouteComponentProps> = ({ history }) => {
  const { t } = useTranslation();
  const [fragments, setFragments] = useState<IPromptFragment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { sort, onSort, items: sortedFragments } = useTableSort({ data: fragments });

  const authUser = useAuthUser();
  const [selectedOperatorId, setSelectedOperatorId] = useState<string>('');

  const { organizationOptions, organizationsLoading } = useOrganizationOptions({
    withPlaceholder: true,
    withBusinessId: true,
  });

  const fetchFragments = useCallback(async () => {
    setLoading(true);
    try {
      const data: any = {};
      if (selectedOperatorId) {
        data.operatorId = selectedOperatorId;
      }
      const response = await axios.post('/json/promptfragments/list', data);
      setFragments(response.data || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data || "Virhe ladattaessa prompteja");
    } finally {
      setLoading(false);
    }
  }, [selectedOperatorId]);

  useEffect(() => {
    fetchFragments();
  }, [fetchFragments]);

  const newHandler = () => {
    history.push(Routes.PROMPT_FRAGMENT("new"));
  };

  const rowClickHandler = (id: string) => {
    history.push(Routes.PROMPT_FRAGMENT(id));
  };

  return (
    <Container>
      {error && <Alert>{error}</Alert>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Promptit</h2>
        <Button onClick={newHandler}>{t(ETranslation.COMMON_ADD)}</Button>
      </div>

      {authUser?.isPrimary && (
        <div style={{ marginBottom: '1rem', maxWidth: '300px' }}>
          <Input
            type={EInputType.reactSelect}
            labelTranslation={ETranslation.COMMON_OPERATOR_NAME}
            value={selectedOperatorId}
            options={organizationOptions}
            inputName="operator"
            loading={organizationsLoading}
            onChange={(value) => setSelectedOperatorId(value as string)}
          />
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : fragments.length > 0 && sortedFragments ? (
        <Table hover sortContext={{ sort, onSort }}>
          <thead>
            <tr>
              <TableTh sort="operator.name">Operaattori</TableTh>
              <TableTh sort="service.name">Palvelu</TableTh>
              <TableTh sort="usage">Käyttötarkoitus</TableTh>
              <TableTh sort="text">Sisältö</TableTh>
            </tr>
          </thead>
          <tbody>
            {sortedFragments.map((fragment) => (
              <tr
                key={fragment.id}
                onClick={() => rowClickHandler(fragment.id)}
              >
                <td>{fragment.operator?.name || ""}</td>
                <td>{fragment.service?.name || ""}</td>
                <td>{fragment.usage}</td>
                <td>
                  {fragment.text && fragment.text.length > 50
                    ? `${fragment.text.substring(0, 50)}...`
                    : fragment.text}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p>Ei prompteja löytynyt.</p>
      )}
    </Container>
  );
};

export default PromptFragments;
